import { getUserList, updateUser, updateUserImage } from "api/userManagement";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import AvatarComp from "themes/elements/Avatar";
import InputComp from "themes/elements/Input";
import ModalComp from "themes/elements/Modal";
import ProfileUpload from "themes/elements/ProfileUpload";
import SelectComp from "themes/elements/Select";
import Toggle from "themes/elements/Toggle";
import style from "../UserDetails/user_details.module.scss";
import ModalFooter from "../modalFooter";
import { USER_TYPE } from "./config";

function EditUser({ open, setOpen, data, fetchUsers }) {
  const { rolesData } = useSelector((state) => state.users);

  const [localData, setLocalData] = useState(data); // Keep the local data state

  const fetchUser = async () => {
    try {
      const response = await getUserList({
        status: 2,
        roleId: -1,
        page: 1,
        perPage: 10,
        limit: 10,
        email: data.email,
        userType: 3,
      });
      if (response && response.data) {
        setLocalData(response.data.users[0]); // Update local data after fetching
        // For debugging
      }
    } catch (error) {}
  };
  // Initialize state for each input
  const [userType, setUserType] = useState(
    data?.user_type === 1 ? USER_TYPE[0]?.label : USER_TYPE[1]?.label
  );
  const [userFileList, setUserFileList] = useState([]);

  const [userTypeValue, setUserTypeValue] = useState(data?.user_type);
  const [roleValue, setRoleValue] = useState(data?.role_name);

  const [firstName, setFirstName] = useState(data?.user_first_name || null);
  const [lastName, setLastName] = useState(data?.user_last_name || null);
  const [email, setEmail] = useState(data?.email || null);
  const [role, setRole] = useState(data?.role_id || null);
  const [image, setImage] = useState(data?.user_image || null);
  const [previewImage, setPreviewImage] = useState(image);

  const [isActive, setIsActive] = useState(
    data?.user_status_flg === 1 ? true : false || false
  );

  useEffect(() => {
    fetchUser();
    setPreviewImage(data?.user_image);
    setImage(data?.user_image);
    setUserType(
      data?.user_type === 1 ? USER_TYPE[0]?.label : USER_TYPE[1]?.label
    );
    setRoleValue(data?.role_name);
    setFirstName(data?.user_first_name || "");
    setLastName(data?.user_last_name || "");
    setEmail(data?.email || "");
    setRole(data?.role_id || "");
    setUserTypeValue(data?.user_type);
    setIsActive(data?.user_status_flg === 1 ? true : false || false);
  }, [data]);

  // Map roles data for select options
  const roleOptions = rolesData?.map((role) => ({
    value: role?.role_name,
    name: role?.role_name,
    id: role?.role_id,
  }));

  // Find the default role name based on the role_id provided in data
  useEffect(() => {
    if (data?.role_id) {
      const defaultRoleName = rolesData.find(
        (role) => role?.role_id === data?.role_id
      )?.role_name;
      setRoleValue(defaultRoleName); // Setting the default role name
    }
  }, [data?.role_id, rolesData]);

  // Handler functions
  const handleUserTypeChange = (value, item) => {
    setUserType(item);
    setUserTypeValue(value);
  };

  const handleFirstNameChange = (event) => setFirstName(event.target.value);
  const handleLastNameChange = (event) => setLastName(event.target.value);
  // const handleEmailChange = (event) => setEmail(event.target.value);
  const handleRoleChange = (name, item) => {
    setRoleValue(name);
    setRole(item?.id);
  };
  const [saveLoading, setSaveLoading] = useState(false); // Loading state for saving

  const handleToggleActive = () => setIsActive(!isActive);

  const handleSave = async () => {
    setSaveLoading(true);
    let roleOption = roleOptions.find((option) => option.id === role);

    // Check if roleOption is defined and has an id
    if (!roleOption || !roleOption.id || !role || !firstName || !lastName) {
      toast.warning("All fields are required.");
      setSaveLoading(false);
      return; // Stop the function if no role is selected
    }
    const roleData = {
      admin_user_id: data?.admin_user_id,
      user_first_name: firstName,
      user_last_name: lastName,
      role_id: roleOption?.id,
      user_status_flg: isActive ? 1 : 0,
      user_type: userTypeValue,
      object_count: 1,
    };
    try {
      const res = await updateUser(roleData);

      toast.success(res.message);
      fetchUsers();
      setOpen(false);
      setSaveLoading(false);
    } catch (error) {
      setSaveLoading(false);
      setOpen(false);
    } finally {
      setSaveLoading(false);
    }
  };

  const callUpdateImageAPI = async (fileObj, actionType, path, id) => {
    try {
      const payload = {
        admin_user_id: id,
        action: actionType,
      };
      if (actionType !== "delete") {
        payload.images = [
          {
            user_image_name: "user",
            user_image_size: fileObj.size,
            user_image_mimetype: fileObj.type,
            user_image_filepath: path,
          },
        ];
      }
      const res = await updateUserImage(payload);
    } catch (error) {
      message.error("Failed to update user image");
    }
  };

  return (
    <ModalComp
      title="Edit user details"
      footer={
        <ModalFooter
          handlePrimaryBtn={() => setOpen(false)}
          primaryBtnVariant="secondary"
          primaryBtnText="Cancel"
          secondaryBtnLoading={saveLoading}
          handleSecondaryBtn={() => {
            handleSave();
          }}
          secondaryBtnText="Save"
        />
      }
      open={open}
      setOpen={setOpen}
    >
      <div className={`${style["content-wrapper"]} flex gap-6`}>
        {data?.user_type === 1 ? (
          <ProfileUpload
            fetchUsers={fetchUsers}
            fetchUser={fetchUser}
            setPreviewImage={setPreviewImage}
            previewImage={previewImage}
            fileList={userFileList}
            setFileList={setUserFileList}
            picture={image}
            data={localData}
          />
        ) : (
          <div className={style["profile-pic"]}>
            <AvatarComp size={100} blue userDetails>
              {firstName?.slice(0, 1).toUpperCase()}
            </AvatarComp>
          </div>
        )}
        <div className="flex flex-column gap-4 w-full">
          <SelectComp
            disabled={true}
            value={userType}
            defaultValue={userType}
            onChange={handleUserTypeChange}
            data={USER_TYPE}
            label="User type"
            placeholder="Select type"
            allowClear={false}
          />
          <div className="flex align-center gap-4">
            <InputComp
              disabled={data?.user_type === 2}
              value={firstName}
              onChange={handleFirstNameChange}
              layoutClass="w-full"
              label="First name"
              placeholder="First name"
              maxLength={25}
              showCount={data?.user_type === 2 ? false : true}
            />
            <InputComp
              disabled={data?.user_type === 2}
              value={lastName}
              onChange={handleLastNameChange}
              layoutClass="w-full"
              label="Last name"
              placeholder="Last name"
              maxLength={25}
              showCount={data?.user_type === 2 ? false : true}
            />
          </div>
          <InputComp
            // maxLength={50}
            // showCount
            value={email}
            disabled={true}
            // onChange={handleEmailChange}
            label="Email address"
            placeholder="Enter email address"
          />
          <SelectComp
            value={roleValue}
            defaultValue={roleOptions.find((option) => option.id === role)}
            onChange={handleRoleChange}
            placeholder="Select role"
            data={roleOptions}
            label="Role"
            allowClear={false}
          />
          <Toggle
            isSmall
            active={isActive}
            onChange={handleToggleActive}
            label="Active"
          />
        </div>
      </div>
    </ModalComp>
  );
}

export default EditUser;
